import React from 'react'

import {Alert, Icon, Button, H4, H6, Panel, Row, S1, Colors, P} from '../UI/index.js'
import AreaMap from '../map/AreaMap.js'
import OrderConditionGroupsEditor from '../conditions/OrderConditionGroupsEditor.js'
import {PARTNER_CONDITION_VARIABLES} from '../../../server/functions/orders/orderConditions.js'
import {formSpacing, FormRow, FormStack, Toggle, ColorPicker, Input, Select, MetricInput} from '../UI/forms/index.js'
import t from '../../helpers/Languages.js'
import _ from 'lodash'

/**
 * The settings of one direction of a partner connection: forwarding orders to the partner (outgoing) or receiving
 * orders from the partner (incoming).
 */
export default class PartnerSettingsPanel extends React.PureComponent {
    constructor(props) {
        super(props)

        this.state = this.initialState(props)
    }

    componentDidUpdate(prevProps) {
        const {partnerId, direction, reseller} = this.props

        if (prevProps.partnerId !== partnerId || prevProps.direction !== direction || prevProps.reseller.settings.outgoingPartners !== reseller.settings.outgoingPartners || prevProps.reseller.settings.incomingPartners !== reseller.settings.incomingPartners) {
            this.setState(this.initialState(this.props))
        }
    }

    initialState(props) {
        const {reseller, partnerId, direction} = props

        const activeConnection = (connections) => {
            const connection = connections?.find((connection) => connection._id === partnerId && connection.status === 'active')

            return connection ? structuredClone(connection) : null
        }

        return {
            outgoing: direction === 'outgoing' ? activeConnection(reseller.settings.outgoingPartners) : null,
            incoming: direction === 'incoming' ? activeConnection(reseller.settings.incomingPartners) : null,
            settingsChanged: false
        }
    }

    /**
     * @param {'outgoing' | 'incoming'} direction
     * @param {object} settings
     */
    setSettings(direction, settings) {
        this.setState({[direction]: settings, settingsChanged: true})
    }

    /**
     * @param {'outgoing' | 'incoming'} direction
     * @param {string} path
     * @param {{target: HTMLInputElement}} event
     */
    onChangeValue(direction, path, event) {
        const settings = {...this.state[direction]}
        _.set(settings, path, event.target.value)
        this.setSettings(direction, settings)
    }

    /**
     * @param {'outgoing' | 'incoming'} direction
     * @param {string} path
     * @param {{target: HTMLInputElement}} event
     */
    onChangeChecked(direction, path, event) {
        const settings = {...this.state[direction]}
        _.set(settings, path, !!event.target.checked)
        this.setSettings(direction, settings)
    }

    /**
     * @param {'outgoing' | 'incoming'} direction
     * @param {string} path
     * @param {string} value
     */
    onChangeArray(direction, path, value) {
        const settings = {...this.state[direction]}

        const array = [..._.get(settings, path, [])]

        const index = array.indexOf(value)

        if (index > -1) {
            array.splice(index, 1)
        } else {
            array.push(value)
        }

        _.set(settings, path, array)

        this.setSettings(direction, settings)
    }

    /**
     * Draws a new (own) delivery zone and selects it in the delivery zones of the given direction
     * @param {'outgoing' | 'incoming'} direction
     * @param {object} geoJSON
     */
    async onCreateDeliveryZone(direction, geoJSON) {
        const deliveryZone = await this.props.onCreateDeliveryZone(geoJSON)

        if (deliveryZone) {
            const settings = {...this.state[direction], deliveryZones: [...this.state[direction].deliveryZones, deliveryZone.id]}

            if (direction === 'outgoing') {
                settings.useCustomDeliveryZones = true
            }

            this.setSettings(direction, settings)
        }
    }

    /**
     * The conditions of a direction: the default ones of the reseller or the connection's own conditions
     * @param {'outgoing' | 'incoming'} direction
     * @param {object} settings
     * @param {string} info
     */
    renderConditions(direction, settings, info) {
        const {reseller, customers} = this.props

        return (
            <>
                <H6 style={{marginBottom: 12, marginTop: 36}}>{direction === 'outgoing' ? t('partnerOutgoingConditions') : t('partnerIncomingConditions')}</H6>
                <P style={{marginBottom: 24}}>{info}</P>

                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <Select
                        label={t('conditions')}
                        value={settings.customConditions || 'default'}
                        noSort
                        options={[
                            {value: 'default', title: t('partnerUseDefault')},
                            {value: 'custom', title: t('partnerCustomConditions')}
                        ]}
                        onChange={(event) => this.onChangeValue(direction, 'customConditions', event)}
                    />
                </FormStack>

                {settings.customConditions === 'custom' &&
                    <OrderConditionGroupsEditor
                        conditionGroups={settings.conditionGroups || []}
                        matchAll={settings.conditionsMatchAll !== false}
                        variables={PARTNER_CONDITION_VARIABLES[direction]}
                        customers={customers || {}}
                        reseller={reseller}
                        onChange={(conditionGroups, conditionsMatchAll) => this.setSettings(direction, {...settings, conditionGroups, conditionsMatchAll})}
                    />
                }
            </>
        )
    }

    onSubmit() {
        const {direction} = this.props

        this.props.onSave(this.state[direction])
    }

    render() {
        const {reseller, partners, partnerId, direction} = this.props
        const {outgoing, incoming, settingsChanged} = this.state

        if (!outgoing && !incoming) {
            return null
        }

        const partner = partners?.[partnerId]
        const defaults = reseller.settings.defaultPartnerSettings

        const restrictedDeliveryZones = outgoing ? reseller.settings.deliveryZones.filter((deliveryZone) => outgoing.deliveryZones?.includes(deliveryZone.id)) : []

        return (
            <Panel shadow style={{flex: '1 1 0', minWidth: 600, maxWidth: 800, padding: 24, height: 'fit-content'}}>
                <Row style={{alignItems: 'center', marginBottom: 36}}>
                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, justifyContent: 'center', minWidth: 'auto'}}>
                        <img src={partner?.logo} style={{height: 32, maxWidth: 256, objectFit: 'contain'}} />
                    </Row>
                    <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, justifyContent: 'center', minWidth: 'auto'}}>
                        <H4>{partner?.name}</H4>
                    </Row>
                </Row>

                {outgoing &&
                    <>
                        <H6 style={{marginBottom: 24}}>{t('partnerOutgoingSection', partner?.name || '')}</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <Toggle
                                label={t('partnerShowScanColor')}
                                info={t('partnerShowScanColorInfo')}
                                checked={outgoing.useCustomScanColor}
                                onChange={(event) => this.onChangeChecked('outgoing', 'useCustomScanColor', event)}
                            />

                            {outgoing.useCustomScanColor &&
                                <Row style={{alignItems: 'center', columnGap: formSpacing.columnGap}}>
                                    <ColorPicker
                                        value={outgoing.customScanColor}
                                        colors={Colors.routeColors}
                                        onChange={(event) => this.onChangeValue('outgoing', 'customScanColor', event)}
                                    />

                                    <P
                                        style={{
                                            height: 36,
                                            flex: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: 4,
                                            backgroundColor: outgoing.customScanColor || '',
                                            color: Colors.isDarkBackground(outgoing.customScanColor || '') ? 'white' : 'black'
                                        }}
                                    >
                                        {partner?.name}
                                    </P>
                                </Row>
                            }

                            <Input
                                label={t('partnerCreditorCode')}
                                info={t('partnerCreditorCodeInfo')}
                                value={outgoing.creditorCode}
                                onChange={(event) => this.onChangeValue('outgoing', 'creditorCode', event)}
                            />
                        </FormStack>

                        <H6 style={{marginBottom: 12, marginTop: 36}}>{t('partnerDeliveryZoneOf', partner?.name || '')}</H6>

                        <div style={{height: 500, marginBottom: 12}}>
                            <AreaMap
                                center={partner?.postalCodesPosition || partner?.companyAddress?.position || reseller.settings.companyAddress.position}
                                layers={[
                                    ...(partner?.deliveryZones || []),
                                    ...(outgoing.useCustomDeliveryZones ? restrictedDeliveryZones : [])
                                ]}
                                onCreate={(geoJSON) => this.onCreateDeliveryZone('outgoing', geoJSON)}
                            />
                        </div>

                        {(partner?.deliveryZones?.length > 0 || !partner?.postalCodes) &&
                            <Row style={{alignItems: 'center', gap: 6, marginBottom: 6}}>
                                <Icon icon='mdi mdi-circle' style={{marginRight: 6, fontSize: 18, color: partner?.deliveryZones?.[0]?.color || Colors.textDark}} />
                                <S1>{t('partnerDeliveryZoneOf', partner?.name || '')}</S1>
                            </Row>
                        }

                        {partner?.postalCodes &&
                            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                                <Input
                                    readOnly
                                    label={partner.deliveryZones?.length > 0 ? t('partnerAdditionalPostalCodesOf', partner.name) : t('partnerPostalCodesOf', partner.name)}
                                    value={partner.postalCodes}
                                />
                            </FormStack>
                        }

                        <FormStack style={{marginTop: formSpacing.section, marginBottom: formSpacing.rowGap}}>
                            <Toggle
                                label={t('partnerUseRestrictedDeliveryZone')}
                                info={t('partnerRestrictedDeliveryZoneInfo', partner?.name || t('partnerPartner').toLowerCase())}
                                checked={outgoing.useCustomDeliveryZones}
                                onChange={(event) => this.onChangeChecked('outgoing', 'useCustomDeliveryZones', event)}
                            />
                        </FormStack>

                        {outgoing.useCustomDeliveryZones &&
                            <>
                                <P style={{marginBottom: 24}}>
                                    {t('partnerRestrictedDeliveryZoneText', partner?.name || t('partnerPartner').toLowerCase())}
                                </P>

                                {reseller.settings.deliveryZones.length === 0 &&
                                    <Alert variant='info' style={{marginBottom: 24}}>
                                        {t('partnerNoDeliveryZones')}
                                    </Alert>
                                }

                                {reseller.settings.deliveryZones.map((deliveryZone) => {
                                    return (
                                        <Row key={deliveryZone.id} style={{alignItems: 'center', marginBottom: 12}}>
                                            <Icon icon='mdi mdi-circle' style={{marginRight: 6, fontSize: 24, color: deliveryZone.color}} />

                                            <S1 style={{flex: 1}}>{deliveryZone.name}</S1>

                                            <Toggle
                                                style={{flex: 0}}
                                                checked={outgoing.deliveryZones.includes(deliveryZone.id)}
                                                onChange={() => this.onChangeArray('outgoing', 'deliveryZones', deliveryZone.id)}
                                            />
                                        </Row>
                                    )
                                })}

                                <FormStack style={{marginTop: formSpacing.rowGap, marginBottom: formSpacing.rowGap}}>
                                    <Input
                                        label={t('partnerAdditionalPostalCodes')}
                                        info={t('partnerOutgoingPostalCodesInfo', partner?.name || t('partnerPartner').toLowerCase())}
                                        value={outgoing.postalCodes || ''}
                                        onChange={(event) => this.onChangeValue('outgoing', 'postalCodes', event)}
                                    />
                                </FormStack>
                            </>
                        }

                        {this.renderConditions('outgoing', outgoing, t('partnerOutgoingConditionsInfo', partner?.name || t('partnerPartner').toLowerCase()))}

                        <FormStack style={{marginTop: formSpacing.section, marginBottom: formSpacing.rowGap}}>
                            <Select
                                label={t('partnerCommunication')}
                                value={outgoing.communication || 'default'}
                                options={[
                                    {value: 'default', title: t('partnerUseDefaultValue', defaults.outgoing.communication === 'reseller' ? reseller.settings.accountName || reseller.name : partner?.name)},
                                    {value: 'reseller', title: reseller.settings.accountName || reseller.name},
                                    {value: 'partner', title: partner?.name}
                                ]}
                                onChange={(event) => this.onChangeValue('outgoing', 'communication', event)}
                            />

                            <Select
                                label={t('partnerAllowEditOrderOf', partner?.name)}
                                value={outgoing.allowEditOrder || 'default'}
                                options={[
                                    {value: 'default', title: t('partnerUseDefaultValue', defaults.outgoing.allowEditOrder === 'no' ? t('no') : defaults.outgoing.allowEditOrder === 'addressOnly' ? t('partnerAddressOnly') : t('yes'))},
                                    {value: 'yes', title: t('yes')},
                                    {value: 'addressOnly', title: t('partnerAddressOnly')},
                                    {value: 'no', title: t('no')}
                                ]}
                                onChange={(event) => this.onChangeValue('outgoing', 'allowEditOrder', event)}
                            />

                            <Select
                                label={t('partnerAutoForwardOrders')}
                                info={t('partnerAutoForwardOrdersInfo')}
                                value={outgoing.autoForwardOrders || 'default'}
                                options={[
                                    {value: 'default', title: t('partnerUseDefaultValue', defaults.outgoing.autoForwardOrders ? t('yes') : t('no'))},
                                    {value: 'yes', title: t('yes')},
                                    {value: 'no', title: t('no')}
                                ]}
                                onChange={(event) => this.onChangeValue('outgoing', 'autoForwardOrders', event)}
                            />
                        </FormStack>
                    </>
                }

                {incoming &&
                    <>
                        <H6 style={{marginBottom: 24}}>{t('partnerIncomingSection', partner?.name || '')}</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <Select
                                label={t('partnerShowRouteInfo')}
                                info={t('partnerShowRouteInfoInfo')}
                                value={incoming.showRouteInfo || 'default'}
                                options={[
                                    {value: 'default', title: t('partnerUseDefaultValue', defaults.incoming.showRouteInfo ? t('yes') : t('no'))},
                                    {value: 'yes', title: t('yes')},
                                    {value: 'no', title: t('no')}
                                ]}
                                onChange={(event) => this.onChangeValue('incoming', 'showRouteInfo', event)}
                            />
                        </FormStack>

                        <H6 style={{marginBottom: 12, marginTop: 36}}>{t('deliveryZone')}</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <Select
                                label={t('partnerOwnDeliveryZone')}
                                value={incoming.customDeliveryZones || 'default'}
                                options={[
                                    {value: 'default', title: t('partnerUseDefault')},
                                    {value: 'custom', title: t('partnerCustomDeliveryZone')}
                                ]}
                                onChange={(event) => this.onChangeValue('incoming', 'customDeliveryZones', event)}
                            />
                        </FormStack>

                        {incoming.customDeliveryZones === 'custom' &&
                            <>
                                <div style={{height: 500, marginBottom: 24}}>
                                    <AreaMap
                                        center={reseller.settings.companyAddress.position}
                                        layers={reseller.settings.deliveryZones.filter((deliveryZone) => {
                                            return incoming.deliveryZones.includes(deliveryZone.id)
                                        })}
                                        onCreate={(geoJSON) => this.onCreateDeliveryZone('incoming', geoJSON)}
                                    />
                                </div>

                                <P style={{marginBottom: 24}}>
                                    {t('partnerDeliveryZoneText', {partnerName: partner?.name, name: reseller.settings.accountName || reseller.name})}
                                </P>

                                {reseller.settings.deliveryZones.map((deliveryZone) => {
                                    return (
                                        <Row key={deliveryZone.id}style={{alignItems: 'center', marginBottom: 12}}>
                                            <Icon icon='mdi mdi-circle' style={{marginRight: 6, fontSize: 24, color: deliveryZone.color}} />

                                            <S1 style={{flex: 1}}>{deliveryZone.name}</S1>

                                            <Toggle
                                                style={{flex: 0}}
                                                checked={incoming.deliveryZones.includes(deliveryZone.id)}
                                                onChange={() => this.onChangeArray('incoming', 'deliveryZones', deliveryZone.id)}
                                            />
                                        </Row>
                                    )
                                })}
                            </>
                        }

                        <FormStack style={{marginTop: formSpacing.rowGap, marginBottom: formSpacing.rowGap}}>
                            <Input
                                label={t('partnerAdditionalPostalCodes')}
                                info={t('partnerIncomingPartnerPostalCodesInfo')}
                                placeholder={incoming.customDeliveryZones === 'custom' ? '' : defaults.incoming.postalCodes}
                                value={incoming.postalCodes || ''}
                                onChange={(event) => this.onChangeValue('incoming', 'postalCodes', event)}
                            />
                        </FormStack>

                        <H6 style={{marginBottom: 12, marginTop: 36}}>{t('partnerMaxDimensions')}</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <FormRow>
                                <MetricInput
                                    label={t('partnerMaxLength')}
                                    placeholder={defaults.incoming.maxLength}
                                    value={incoming.maxLength}
                                    divisionFactor={10}
                                    onChange={(event) => this.onChangeValue('incoming', 'maxLength', event)}
                                    append='cm'
                                />

                                <MetricInput
                                    label={t('partnerMaxWidth')}
                                    placeholder={defaults.incoming.maxWidth}
                                    value={incoming.maxWidth}
                                    divisionFactor={10}
                                    onChange={(event) => this.onChangeValue('incoming', 'maxWidth', event)}
                                    append='cm'
                                />

                                <MetricInput
                                    label={t('partnerMaxHeight')}
                                    placeholder={defaults.incoming.maxHeight}
                                    value={incoming.maxHeight}
                                    divisionFactor={10}
                                    onChange={(event) => this.onChangeValue('incoming', 'maxHeight', event)}
                                    append='cm'
                                />
                            </FormRow>

                            <FormRow>
                                <MetricInput
                                    label={t('partnerMaxWeight')}
                                    placeholder={defaults.incoming.maxWeight}
                                    value={incoming.maxWeight}
                                    divisionFactor={1000}
                                    onChange={(event) => this.onChangeValue('incoming', 'maxWeight', event)}
                                    append='kg'
                                />

                                <MetricInput
                                    label={t('partnerMaxVolume')}
                                    placeholder={defaults.incoming.maxVolume}
                                    value={incoming.maxVolume}
                                    onChange={(event) => this.onChangeValue('incoming', 'maxVolume', event)}
                                    append='dm3'
                                />
                            </FormRow>
                        </FormStack>

                        {this.renderConditions('incoming', incoming, t('partnerIncomingConditionsInfo', partner?.name || t('partnerPartner').toLowerCase()))}
                    </>
                }

                <div style={{display: 'flex', justifyContent: 'flex-end', columnGap: formSpacing.columnGap, marginTop: 48}}>
                    <Button variant='error' onClick={() => this.props.onRemove(direction)}>
                        {t('partnerDeactivate')}
                    </Button>

                    <div style={{flex: 1}} />

                    <Button onClick={this.onSubmit.bind(this)} disabled={!settingsChanged}>
                        {t('save')}
                    </Button>
                </div>
            </Panel>
        )
    }
}
